import { queryOptions } from "@tanstack/react-query";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import moment from "moment";
import { useDates } from "../../../../dates/DatesContext";
import { useAppSelector } from "../../../../redux/hooks";
import { apiClient } from "../../../../zodios/client";
import type { SponsoredAdsAudienceFilter } from "../../../../zodios/apiDefinitions/api/sponsoredAdsAudience";
import { adaptDatasetsFilters } from "../../../../utils/datasetsFilters";
import { resolveServerId } from "../../../merchandising/utils/metrics";
import type { SponsoredAdsAudienceAdType } from "../../audience_report/sponsoredAdsAudienceGroupings";

function toSortString(sorting: SortingState): string | undefined {
  if (sorting.length === 0) return undefined;
  return sorting.map(({ id, desc }) => `${desc ? "-" : ""}${resolveServerId(id)}`).join(",");
}

type ChartOrTotalsParams = {
  filters?: ColumnFiltersState;
  search?: string;
};

export function useGetSponsoredAdsAudienceByDate(adType: SponsoredAdsAudienceAdType = "sponsoredProduct") {
  const { start, end, preStart, preEnd, group } = useDates();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const table = adType === "sponsoredBrand" ? "sbaudience" : "spaudience";

  return (params?: ChartOrTotalsParams) => {
    const extraFilters = adaptDatasetsFilters(params?.filters);
    const searchFilter: SponsoredAdsAudienceFilter[] =
      params?.search ? [{ id: "segment_name", value: { op: "contains", value: params.search } }] : [];

    const filters: SponsoredAdsAudienceFilter[] = [
      { id: "start_date", value: { op: "gte", value: startDate } },
      { id: "start_date", value: { op: "lte", value: endDate } },
      { id: "date_prev", value: { op: "gte", value: preStartDate } },
      { id: "date_prev", value: { op: "lte", value: preEndDate } },
      { id: "market_place", value: { op: "eq", value: marketPlace } },
      ...extraFilters,
      ...searchFilter,
    ];

    return queryOptions({
      queryKey: [
        { entity: `${adType}AudienceByDate`, dates: { startDate, endDate, preStartDate, preEndDate, group }, filters },
      ],
      queryFn: async () => {
        const res = await apiClient.sponsoredAdsAudienceDatasetsQuery({
          model: table,
          dimensions: [group],
          filters,
        });
        return {
          data: res.data.map((row) => ({
            ...row,
            start_date: row.day ?? row.week ?? row.month ?? "",
          })),
        };
      },
    });
  };
}
export function useGetSponsoredAdsAudienceTotals(adType: SponsoredAdsAudienceAdType = "sponsoredProduct") {
  const { start, end, preStart, preEnd } = useDates();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const table = adType === "sponsoredBrand" ? "sbaudience" : "spaudience";

  return (params?: ChartOrTotalsParams) => {
    const extraFilters = adaptDatasetsFilters(params?.filters);
    const searchFilter: SponsoredAdsAudienceFilter[] =
      params?.search ? [{ id: "segment_name", value: { op: "contains", value: params.search } }] : [];

    const filters: SponsoredAdsAudienceFilter[] = [
      { id: "start_date", value: { op: "gte", value: startDate } },
      { id: "start_date", value: { op: "lte", value: endDate } },
      { id: "date_prev", value: { op: "gte", value: preStartDate } },
      { id: "date_prev", value: { op: "lte", value: preEndDate } },
      { id: "market_place", value: { op: "eq", value: marketPlace } },
      ...extraFilters,
      ...searchFilter,
    ];

    return queryOptions({
      queryKey: [
        { entity: `${adType}AudienceTotals`, dates: { startDate, endDate, preStartDate, preEndDate }, filters },
      ],
      queryFn: async () => {
        const res = await apiClient.sponsoredAdsAudienceDatasetsQuery({
          model: table,
          dimensions: [],
          filters,
        });
        return { data: res.data[0] };
      },
    });
  };
}
export function useGetSponsoredAdsAudienceBySegment(
  dimensions: string | string[] = "segment_name",
  adType: SponsoredAdsAudienceAdType = "sponsoredProduct",
) {
  const dimensionsArray = Array.isArray(dimensions) ? dimensions : [dimensions];
  const needsCampaignJoin = dimensionsArray.includes("campaign_id");
  const { start, end, preStart, preEnd } = useDates();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const table = adType === "sponsoredBrand" ? "sbaudience" : "spaudience";

  type Params = {
    pagination: PaginationState;
    sorting: SortingState;
    filters?: ColumnFiltersState;
    search?: string;
  };

  return (params: Params) => {
    const columnFilters = adaptDatasetsFilters(params.filters);
    const searchFilter: SponsoredAdsAudienceFilter[] =
      params.search ? [{ id: "segment_name", value: { op: "contains", value: params.search } }] : [];

    const filters: SponsoredAdsAudienceFilter[] = [
      { id: "start_date", value: { op: "gte", value: startDate } },
      { id: "start_date", value: { op: "lte", value: endDate } },
      { id: "date_prev", value: { op: "gte", value: preStartDate } },
      { id: "date_prev", value: { op: "lte", value: preEndDate } },
      { id: "market_place", value: { op: "eq", value: marketPlace } },
      ...columnFilters,
      ...searchFilter,
    ];

    return queryOptions({
      queryKey: [
        {
          entity: `${adType}AudienceBySegment`,
          dimensions,
          params,
          dates: { startDate, endDate, preStartDate, preEndDate },
        },
      ],
      queryFn: async () => {
        const res = await apiClient.sponsoredAdsAudienceDatasetsQuery({
          model: table,
          dimensions,
          filters,
          sort: toSortString(params.sorting),
          page: params.pagination.pageIndex + 1,
          limit: params.pagination.pageSize,
          include_count: true,
          ...(needsCampaignJoin ? { entity_mode: "join" } : {}),
        });
        return { data: res.data, count: res.pagination.total ?? 0 };
      },
      staleTime: 30000,
    });
  };
}
